import React, { useState } from 'react';
import { enterKeyHandler } from 'window-utils/lib/keyHandlerUtils';
import { getIconClassName } from '@uifabric/styling/lib/index';
import { getRTL } from '@uifabric/utilities';
import { TreeNodeData } from '../schema/TreeNodeData';

export interface TreeNodeProps {
    nodeId: string;
    depth: number;
    getNodeData?: (nodeId: string) => TreeNodeData;
    onKeyDown?: (e: any) => void;
    refValue?: React.RefObject<any>;
}

const classNames = require('classnames/bind');
const styles = require('./TreeNode.scss');

const TREENODE_BASE_PADDING = 12;
const TREENODE_DEPTH_PADDING = 28;

/**
 * Component which renders TreeNode.
 * @param props TreeNodeProps
 */
const TreeNode = (props: TreeNodeProps) => {
    const { getNodeData, nodeId, depth, refValue, onKeyDown } = props;
    const {
        displayName,
        rightCharm,
        isExpanded,
        isSelected,
        childNodeIds,
        onClick,
        iconProps,
        ariaLabelForNode,
        treeNodeViewId,
    } = getNodeData(nodeId);

    const [isNodeExpanded, setIsNodeExpanded] = useState(!!isExpanded);

    const treePadding = depth * TREENODE_DEPTH_PADDING + TREENODE_BASE_PADDING;
    const treeNodeStyle = getRTL() ? { paddingRight: treePadding } : { paddingLeft: treePadding };

    const onIconClick = (event: React.MouseEvent) => {
        event.stopPropagation();
        setIsNodeExpanded(!isNodeExpanded);
        iconProps && iconProps.onClick && iconProps.onClick(depth);
    };

    const onNodeClick = (event: React.MouseEvent) => {
        event.stopPropagation();
        onClick && onClick(nodeId);
    };

    const renderLeftIcon = () => {
        if (iconProps) {
            const { iconName, onClick, getChevronIconAriaLabel } = iconProps;
            return onClick ? (
                <div
                    role={'button'}
                    tabIndex={0}
                    onClick={onIconClick}
                    onKeyDown={enterKeyHandler(onIconClick)}
                    aria-expanded={isNodeExpanded}
                    aria-label={getChevronIconAriaLabel(isNodeExpanded, displayName)}>
                    <i
                        className={classNames(
                            'ms-Icon',
                            getIconClassName(iconName),
                            styles.icon,
                            styles.chevronIcon
                        )}
                    />
                </div>
            ) : (
                <label
                    htmlFor={treeNodeViewId}
                    className={classNames('ms-Icon', iconName, styles.icon)}
                    aria-hidden={'true'}
                />
            );
        } else {
            return <i className={styles.icon} aria-hidden={'true'} />;
        }
    };

    return (
        <>
            <div
                className={classNames(styles.nodeContainer, { [styles.isSelected]: isSelected })}
                style={treeNodeStyle}>
                {rightCharm ? rightCharm : null}
                <button
                    className={styles.nodeDisplayName}
                    id={treeNodeViewId}
                    aria-label={ariaLabelForNode}
                    onClick={onNodeClick}
                    tabIndex={0}
                    ref={refValue}
                    onKeyDown={onKeyDown}>
                    {displayName}
                </button>
                {renderLeftIcon()}
            </div>
            {isNodeExpanded && childNodeIds
                ? childNodeIds.map(childNodeId => (
                      <TreeNode
                          key={childNodeId}
                          nodeId={childNodeId}
                          getNodeData={getNodeData}
                          depth={depth >= 0 ? depth + 1 : 0}
                      />
                  ))
                : null}
        </>
    );
};

export default TreeNode;
